import { useMemo } from 'react';
import { DashboardLayoutConfig } from '../contracts/layout.contract';
import { DashboardGrid, GridItem } from './DashboardGrid';
import { widgetRegistry } from '../registry/WidgetRegistry';
import { useRole } from '@/shared/contexts/RoleContext';
import { useUser } from '@/shared/hooks/useAuth';
import { widgetIsVisible } from '../../widget-visibility';
import { viewLevelOf, type ViewLevel } from '../../dashboard-resolution';

export interface DashboardLayoutEngineProps {
  layout: DashboardLayoutConfig;
  /**
   * The level-and-grants gate. Omitted, it is derived from the signed-in user,
   * so no caller can forget it.
   */
  visibility?: { user: Parameters<typeof viewLevelOf>[0]; level: ViewLevel };
}

export function DashboardLayoutEngine({ layout, visibility: given }: DashboardLayoutEngineProps) {
  const { can } = useRole();
  const user = useUser();
  const role = user?.role;
  const visibility = given ?? { user, level: viewLevelOf(user) };

  // Filter widgets that are correctly registered and where the user has access
  const activeWidgets = useMemo(() => {
    return layout.widgets
      .map(pos => {
        const registered = widgetRegistry.getWidget(pos.id);
        return { pos, registered };
      })
      .filter(({ registered }) => {
        if (!registered) return false;

        // 1. Permission Engine. Honours the static role map ONLY for layouts
        // that still declare the legacy `permission` field (Accounts today);
        // everything registered after the role-dashboard rebuild declares
        // `workspace` / `actionKey` and is decided in step 2 instead, so a
        // per-person grant reaches the screen. Phase 2 retires this field.
        if (registered.config.permission && !can(registered.config.permission)) return false;

        // 2. Level + effective grants, read from what the server resolved.
        return widgetIsVisible(registered.config, { user: visibility.user, level: visibility.level });
      });
  }, [layout.widgets, can, role, visibility.user, visibility.level]);

  if (activeWidgets.length === 0) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-1.5 rounded-card border border-dashed border-border bg-card p-6 text-center shadow-xs">
        <span className="text-body-sm font-semibold text-on-surface">No tiles for this view yet</span>
        <span className="max-w-md text-body-sm text-on-surface-variant">
          {layout.name} has no widget your access covers. Ask your department head to grant the workspace, or switch to the view you own.
        </span>
      </div>
    );
  }

  return (
    <DashboardGrid>
      {activeWidgets.map(({ pos, registered }) => {
        if (!registered) return null; // Fallback, already filtered
        const Component = registered.component;

        return (
          <GridItem key={pos.id} w={pos.w} h={pos.h}>
            <div className="h-full">
              <Component id={pos.id} config={registered.config} />
            </div>
          </GridItem>
        );
      })}
    </DashboardGrid>
  );
}
